import client from 'providers/fetchClient'

import { formatDocumentNumber, formatCurrency, formatPercentage, formatPhoneNumber } from 'utils/formatters'
import { translateCommissionedCommissionType, translateCommissionedCreditType } from 'utils/translators'

export const create = async ({ file, ...data }) => {
  const formData = new FormData()

  const document = data.document.replace(/\D/g, '')
  const document_type = document.length > 11 ? 'cnpj' : 'cpf'

  formData.set('document', document)
  formData.set('document_type', document_type)
  formData.set('name', data.name)
  formData.set('user.email', data.email)
  formData.set('user.password', data.password)
  formData.set('phone_number', data.phone_number.replace(/\D/g, ''))
  formData.set('is_contract_accepted', data.is_contract_accepted)

  formData.append('file', file)

  await client.post('commissioneds/signup', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const updateRegistration = async ({ file, email, phone_number, document, name }) => {
  const formData = new FormData()

  if (email) {
    formData.set('user.email', email)
  }

  if (phone_number) {
    formData.set('phone_number', phone_number.replace(/\D/g, ''))
  }

  if (document) {
    const cleanDocument = document.replace(/\D/g, '')
    const document_type = cleanDocument.length > 11 ? 'cnpj' : 'cpf'

    formData.set('document', cleanDocument)
    formData.set('document_type', document_type)
  }

  if (name) {
    formData.set('name', name)
  }

  if (file) {
    formData.append('file', file)
  }

  await client.patch('commissioneds/signup', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const fetch = async id => {
  const { data } = await client.get(`/commissioneds/${id}`)

  return {
    ...data,
    reference_code: data.commissioned_code,
    commission_value: data.commission_value || 0,
    credit_value: data.credit_value || 0,
    formatted: {
      document: formatDocumentNumber(data.document),
      commission_type: translateCommissionedCommissionType(data.commission_type),
      commission_value: formatPercentage(data.commission_value),
      credit_type: translateCommissionedCreditType(data.credit_type),
      credit_value: formatCurrency(data.credit_value),
      month_commission: formatCurrency(data.monthly_commission),
      receivable_commision: formatCurrency(data.open_commission),
      phone_number: formatPhoneNumber(data.phone_number),
    },
  }
}

export const fetchAll = async ({ search, sortBy, pagination, filters }) => {
  const params = { search, ...filters }

  if (sortBy && sortBy.length) {
    params.ordering = sortBy.map(({ id, desc }) => (desc ? `-${id}` : id)).join(',')
  }

  if (pagination) {
    params.page = pagination.currentPage
    params.limit = pagination.pageSize
  }

  const { data } = await client.get('/commissioneds', { params })

  const commissioneds = data.data.map(({ document, commission_value, ...commissioned }) => ({
    ...commissioned,
    document: formatDocumentNumber(document),
    commission_value: formatPercentage(commission_value),
    credit_value: formatCurrency(commissioned?.credit_value),
    monthly_commission: formatCurrency(commissioned?.monthly_commission),
    account_balance: formatCurrency(commissioned?.account_balance),
  }))

  return {
    commissioneds,
    pagination: {
      pageSize: data.limit,
      currentPage: data.page,
      totalPages: data.total_pages,
      totalRecords: data.total,
    },
  }
}

export const approve = (id, { reference_code, ...data }) =>
  client.post(`/commissioneds/${id}/approve`, { ...data, commissioned_code: reference_code })

export const reject = (id, { message, ...formData }) => {
  let data = formData

  if (message) {
    data = { ...formData, message }
  }

  return client.post(`/commissioneds/${id}/reject`, data)
}

export const update = (id, data) => client.patch(`/commissioneds/${id}/edit`, data)

export const updateSettings = (id, { phone_number, email, ...data }) =>
  client.patch(`/commissioneds/${id}`, {
    ...data,
    ...(email && { user: { email } }),
    phone_number: phone_number.replace(/\D/g, ''),
  })

export const fetchNewReferenceCode = () =>
  client.get('commissioneds/generate-code').then(response => response.data.code)

export const updatePassword = (id, data) => client.put(`/users/${id}/password`, data)

export const fetchDefaultValues = params =>
  client.get('commissioneds/settings', { params }).then(response => response.data)

export const updateDefaultValues = async data => await client.post('commissioneds/settings', data)

export const getPagbankTax = async () =>
  await client.get('/commissioneds/pagbank-transaction-tax').then(response => response.data)

export const getTermsOfUse = async () => await client.get('/commissioned-contracts/last')

export const fetchCommissionedsList = async ({ search, sortBy, pagination, filters }) => {
  const params = { search, ...filters }

  if (sortBy && sortBy.length) {
    params.ordering = sortBy.map(({ id, desc }) => (desc ? `-${id}` : id)).join(',')
  }

  if (pagination) {
    params.page = pagination.currentPage
    params.limit = pagination.pageSize
  }

  const { data } = await client.get('/commissioneds-list', { params })

  const commissioneds = data.data.map(({ document, commission_value, ...commissioned }) => ({
    ...commissioned,
    document: formatDocumentNumber(document),
    commission_value: formatPercentage(commission_value),
    credit_value: formatCurrency(commissioned?.credit_value),
    monthly_commission: formatCurrency(commissioned?.monthly_commission),
    account_balance: formatCurrency(commissioned?.account_balance),
  }))

  return {
    commissioneds,
    pagination: {
      pageSize: data.limit,
      currentPage: data.page,
      totalPages: data.total_pages,
      totalRecords: data.total,
    },
  }
}

export default {
  create,
  fetch,
  fetchAll,
  approve,
  reject,
  fetchNewReferenceCode,
  updateRegistration,
  update,
  updateSettings,
  updatePassword,
  fetchDefaultValues,
  updateDefaultValues,
  getPagbankTax,
  getTermsOfUse,
  fetchCommissionedsList,
}
